import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Zap, Shield, Settings } from 'lucide-react';
import { services } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Zap: Zap,
  Shield: Shield,
  Settings: Settings,
};

const WhatWeDo = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        '.services-title',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards animation
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          { y: 80, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            delay: index * 0.15,
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-securelec-red/5 rounded-full blur-[200px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="services-title inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            What We Do
          </span>
          <h2 className="services-title text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
            Expert <span className="text-securelec-red">Solutions</span>
          </h2>
          <p className="services-title text-securelec-gray/70 max-w-2xl mx-auto text-lg">
            Comprehensive electrical and security services tailored to your needs
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className="group relative"
              >
                {/* Card */}
                <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full hover:border-securelec-red/50 hover:shadow-xl transition-all duration-500 relative overflow-hidden">
                  {/* Hover Effect */}
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-securelec-red/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-6">
                    <div className="w-16 h-16 bg-securelec-red/10 rounded-2xl flex items-center justify-center group-hover:bg-securelec-red/20 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-securelec-red" />
                    </div>
                  </div>

                  {/* Category Title */}
                  <h3 className="relative z-10 text-2xl font-bold text-securelec-dark mb-6 group-hover:text-securelec-red transition-colors duration-300">
                    {service.category}
                  </h3>

                  {/* Services List */}
                  <ul className="relative z-10 space-y-3 mb-8">
                    {service.services.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-securelec-gray">
                        <div className="w-1.5 h-1.5 bg-securelec-red rounded-full" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <a
                    href={service.href}
                    className="relative z-10 inline-flex items-center gap-2 text-securelec-red hover:text-securelec-dark transition-colors duration-300 font-semibold text-sm group/link"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-2 transition-transform duration-300" />
                  </a>

                  {/* Bottom Border */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-securelec-red to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
